import React, { ReactElement, useState } from 'react';
import { DatePicker, FormInstance, Space, TreeSelect } from 'antd';
import { useTranslation } from 'react-i18next';
import { Button } from 'common/components/antd/Button/Button';
import { Form } from 'common/components/antd/Form/Form';
import { Input } from 'common/components/antd/Input/Input';
import { IS_ADMIN_FE } from 'common/constants/general-access-role.constants';
import { TCounterpartyEntity } from 'domains/counterparties/types/counterparty.types';
import { EmissionHistoryAssetDirection, EmissionHistoryAssetStatus } from 'domains/emission/enums/emission.enum';
import { EReportingEmissionFilterFormKeys } from 'domains/reporting/enum/reporting.enum';
import { EReportingPaymentStatus } from 'domains/reporting/types/reporting.types';
import {
  STitle,
  SControl,
  SSelectRow,
  SContainer,
  SEnhancedSelect,
} from './filters-form.component.style';

type TProps = {
  onSubmit: (values: any) => void;
  counterparties: TCounterpartyEntity[];
  form: FormInstance;
  isLoading: boolean;
};

export const FiltersFormComponent = ({ onSubmit, counterparties, form, isLoading }: TProps): ReactElement => {
  const { t } = useTranslation();

  const STATUS_OPTIONS = [
    {
      value: EmissionHistoryAssetStatus.Created,
      label: t('reporting.emissionHistory.table.column.status.value.created'),
    },
    {
      value: EmissionHistoryAssetStatus.Confirmed,
      label: t('reporting.emissionHistory.table.column.status.value.confirmed'),
    },
    {
      value: EmissionHistoryAssetStatus.Canceled,
      label: t('reporting.emissionHistory.table.column.status.value.canceled'),
    },
  ];

  const DIRECTION_OPTIONS = [
    {
      value: EmissionHistoryAssetDirection.Issue,
      label: t('reporting.emissionHistory.table.column.direction.value.issue'),
    },
    {
      value: EmissionHistoryAssetDirection.Burn,
      label: t('reporting.emissionHistory.table.column.direction.value.burn'),
    },
  ];

  const [statusValue, setStatusValue] = useState<EReportingPaymentStatus | null>(null);
  const [directionValue, setDirectionValue] = useState<EmissionHistoryAssetDirection | null>(null);
  const [counterpartyValue, setCounterpartyValue] = useState<string>('');

  const counterpartySelectDisabled = counterparties?.length === 0;

  const direction = {
    value: directionValue,
    setValue: setDirectionValue,
    onChange: (value: EmissionHistoryAssetDirection): void => {
      setDirectionValue(value);
    },
    options: DIRECTION_OPTIONS,
  };

  const status = {
    value: statusValue,
    setValue: setStatusValue,
    onChange: (value: EReportingPaymentStatus): void => {
      setStatusValue(value);
    },
    options: STATUS_OPTIONS,
  };

  const counterparty = {
    value: counterpartyValue,
    setValue: setCounterpartyValue,
  };

  const treeData = (counterparties || []).map((_counterparty) => ({
    label: _counterparty.name,
    value: _counterparty.guid,
  }));

  return (
    <SContainer>
      <STitle>{t('reporting.emissionHistory.filters.title')}</STitle>
      <Form onFinish={onSubmit} form={form}>
        {IS_ADMIN_FE && (
          <SControl>
            <Form.Item name={EReportingEmissionFilterFormKeys.Counterparty}>
              <TreeSelect
                style={{
                  width: '100%',
                }}
                disabled={counterpartySelectDisabled}
                value={counterparty.value}
                onChange={counterparty.setValue}
                treeData={treeData}
                placeholder={t('reporting.emissionHistory.filters.counterparty.placeholder')}
                allowClear
              />
            </Form.Item>
          </SControl>
        )}
        <SControl>
          <Form.Item name={EReportingEmissionFilterFormKeys.CreatedAt}>
            <DatePicker.RangePicker
              placeholder={[
                t('reporting.emissionHistory.filters.createdAt.from.placeholder'),
                t('reporting.emissionHistory.filters.createdAt.to.placeholder'),
              ]}
            />
          </Form.Item>
        </SControl>
        <SSelectRow>
          <Space.Compact>
            <Form.Item name={EReportingEmissionFilterFormKeys.AmountMin}>
              <Input placeholder={t('reporting.emissionHistory.filters.amount.value.min.placeholder')!} />
            </Form.Item>
            <Form.Item name={EReportingEmissionFilterFormKeys.AmountMax}>
              <Input placeholder={t('reporting.emissionHistory.filters.amount.value.max.placeholder')!} />
            </Form.Item>
          </Space.Compact>
        </SSelectRow>
        <Form.Item name={EReportingEmissionFilterFormKeys.Direction}>
          <SEnhancedSelect
            size="middle"
            value={direction.value}
            placeholder={t('reporting.emissionHistory.filters.direction.placeholder')}
            onChange={direction.onChange}
            options={direction.options}
            allowClear
          />
        </Form.Item>
        <Form.Item name={EReportingEmissionFilterFormKeys.Status}>
          <SEnhancedSelect
            size="middle"
            value={status.value}
            placeholder={t('reporting.emissionHistory.filters.status.placeholder')}
            onChange={status.onChange}
            options={status.options}
            allowClear
          />
        </Form.Item>
        <Button type="primary" htmlType="submit" width="100%" disabled={isLoading} loading={isLoading}>
          {t('reporting.emissionHistory.filters.button.filter')}
        </Button>
      </Form>
    </SContainer>
  );
};
